import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useEffect, useRef, useState, type VideoHTMLAttributes} from 'react'
import {
  ArrowLeftIcon,
  CopilotIcon,
  PeopleIcon,
  BookIcon,
  DeviceMobileIcon,
  IterationsIcon,
  ColumnsIcon,
  PlayIcon,
} from '@primer/octicons-react'
import {
  ThemeProvider,
  Hero,
  Grid,
  Image,
  LogoSuite,
  AnchorNav,
  SectionIntro,
  FAQ,
  FAQGroup,
  Bento,
  Link,
  Box,
  Text,
  River,
  Heading,
  Label,
  RiverBreakout,
  Timeline,
  Button,
  UnorderedList,
  Stack,
  Card,
  AnimationProvider,
  OrderedList,
} from '@primer/react-brand'

interface Payload {
  copilotSignupPath: string
  copilotForBusinessSignupPath: string
  copilotContactSalesPath: string
  copilotHeroSignupPath: string
  isCopilotPlansPageEnabled: boolean
  copilotHydroTracking: string
  copilotVideoHydroTracking: string
  copilotConvoHydroTracking: string
  copilotSuggestionsHydroTracking: string
  copilotDocsHydroTracking: string
  copilotPullRequestsHydroTracking: string
}

export function DefaultIndex(payload: Payload) {
  const {
    copilotSignupPath,
    copilotForBusinessSignupPath,
    copilotContactSalesPath,
    copilotHeroSignupPath,
    isCopilotPlansPageEnabled,
    copilotHydroTracking,
    copilotVideoHydroTracking,
    copilotConvoHydroTracking,
    copilotSuggestionsHydroTracking,
    copilotDocsHydroTracking,
    copilotPullRequestsHydroTracking,
  } = payload

  // Hero Video player
  //
  // Video player with custom controls for the Hero

  const {heroVideoLg} = useRoutePayload<{heroVideoLg: string}>()
  const {heroVideoLgPoster} = useRoutePayload<{heroVideoLgPoster: string}>()
  const {heroVideoSm} = useRoutePayload<{heroVideoSm: string}>()

  const videoLgRef = useRef<HTMLVideoElement>(null)
  const videoSmRef = useRef<HTMLVideoElement>(null)
  const [videoState, setVideoState] = useState('playing')
  const [videoButtonLabel, setVideoButtonLabel] = useState('Pause')
  const [VideoIcon, setVideoIcon] = useState(() => ColumnsIcon)

  const handleVideoStateChange = () => {
    if (videoState === 'playing') {
      setVideoState('paused')
      setVideoButtonLabel('Play')
      setVideoIcon(() => PlayIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.pause()
        videoSmRef.current.pause()
      }
    } else if (videoState === 'paused') {
      setVideoState('playing')
      setVideoButtonLabel('Pause')
      setVideoIcon(() => ColumnsIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.play()
        videoSmRef.current.play()
      }
    } else if (videoState === 'ended') {
      setVideoState('playing')
      setVideoButtonLabel('Pause')
      setVideoIcon(() => ColumnsIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.currentTime = 0
        videoSmRef.current.currentTime = 0
        videoLgRef.current.play()
        videoSmRef.current.play()
      }
    }
  }

  const handleVideoStateEnd = () => {
    setVideoState('ended')
    setVideoButtonLabel('Replay')
    setVideoIcon(() => IterationsIcon)
  }

  // Auto-play videos
  //
  // Starts playing videos when they're in view

  interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
    src: string
    poster: string
  }

  function AutoPlayVideo({src, poster, ...props}: VideoProps) {
    const videoRef = useAutoplayVideo()

    return (
      <video ref={videoRef} playsInline={true} muted={true} preload="none" poster={poster} {...props}>
        <source src={src} type="video/mp4; codecs=avc1.4d002a" />
      </video>
    )
  }

  function useAutoplayVideo() {
    const videoRef = useRef<HTMLVideoElement | null>(null)

    useEffect(() => {
      const currentVideoRef = videoRef.current

      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (currentVideoRef) currentVideoRef.play()
            } else {
              if (currentVideoRef) currentVideoRef.pause()
            }
          }
        },
        {
          threshold: 0.5, // Play once 50% of the height is...
          rootMargin: `-25% 0% -25% 0%`, // ...in the center 50% of the viewport
        },
      )

      if (currentVideoRef) {
        observer.observe(currentVideoRef)
      }

      return () => {
        if (currentVideoRef) {
          observer.unobserve(currentVideoRef)
        }
      }
    }, [])

    return videoRef
  }

  // Pricing deep link
  //
  // This is an alternative to using `href="#pricing"` on the Hero "Compare plans" button
  // It seems deep linking currently doesn't work reliably, so we're using `scrollTo()` instead

  const ctaPricingRef = useRef<HTMLButtonElement>(null)
  const sectionPricingRef = useRef<HTMLElement>(null)

  const handlePricingClick = (event: Event) => {
    event.preventDefault()

    if (sectionPricingRef.current) {
      // Set focus to the pricing section
      sectionPricingRef.current.setAttribute('tabindex', '-1')
      sectionPricingRef.current.focus({preventScroll: true})

      // Then scroll to the pricing section
      window.scrollTo({
        top: sectionPricingRef.current.offsetTop,
      })
    }
  }

  useEffect(() => {
    const currentCtaPricingRef = ctaPricingRef.current

    if (currentCtaPricingRef) {
      currentCtaPricingRef.addEventListener('click', handlePricingClick)
    }

    return () => {
      if (currentCtaPricingRef) {
        currentCtaPricingRef.removeEventListener('click', handlePricingClick)
      }
    }
  }, [])

  return (
    <>
      <ThemeProvider colorMode="dark" className="lp-Copilot">
        <section id="hero" className="lp-Section lp-Section--hero">
          <Grid>
            <Grid.Column span={12}>
              <Hero data-hpc align="center" className="lp-Hero">
                <Hero.Label size="large" leadingVisual={<CopilotIcon />}>
                  GitHub Copilot
                </Hero.Label>
                <Hero.Heading size="1" weight="bold" className="lp-Hero-heading">
                  The world’s most widely adopted AI developer tool.
                </Hero.Heading>
                <div className="lp-Hero-ctaButtons">
                  {isCopilotPlansPageEnabled ? (
                    <Hero.PrimaryAction href="/features/copilot/plans" className="Button--heroCta">
                      Get started with Copilot
                    </Hero.PrimaryAction>
                  ) : (
                    <>
                      <Hero.PrimaryAction href={copilotHeroSignupPath} className="Button--heroCta">
                        Buy now
                      </Hero.PrimaryAction>
                      <Hero.SecondaryAction
                        href=""
                        hasArrow={false}
                        className="Button--heroCta lp-Button--onColoredBg"
                        ref={ctaPricingRef}
                      >
                        Compare plans
                      </Hero.SecondaryAction>
                    </>
                  )}
                </div>
              </Hero>

              <Box className="lp-Hero-visual">
                <a
                  href="https://docs.github.com/copilot/github-copilot-chat/about-github-copilot-chat#about-github-copilot-chat"
                  data-analytics-event={copilotVideoHydroTracking}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <Box className="lp-Hero-videoContainer">
                    <video
                      autoPlay={true}
                      playsInline={true}
                      muted={true}
                      className="lp-Hero-video lp-Hero-video--landscape hide-reduced-motion"
                      width="1248"
                      height="735"
                      poster={heroVideoLgPoster}
                      ref={videoLgRef}
                      onEnded={() => handleVideoStateEnd()}
                    >
                      <source src={heroVideoLg} type="video/mp4; codecs=avc1.4d002a" />
                    </video>

                    <video
                      autoPlay={true}
                      playsInline={true}
                      muted={true}
                      className="lp-Hero-video lp-Hero-video--portrait hide-reduced-motion"
                      width="539.5"
                      height="682"
                      ref={videoSmRef}
                      onEnded={() => handleVideoStateEnd()}
                    >
                      <source src={heroVideoSm} type="video/mp4; codecs=avc1.4d002a" />
                    </video>

                    <Image
                      className="lp-Hero-videoImage hide-no-pref-motion"
                      src="/images/modules/site/copilot/hero.jpg"
                      alt="Editor with GitHub Copilot Chat"
                      width="1248"
                      loading="lazy"
                    />
                  </Box>
                </a>

                <Box paddingBlockStart={12} className="lp-Hero-videoPlayer">
                  <Button
                    leadingVisual={VideoIcon}
                    variant="subtle"
                    hasArrow={false}
                    className="lp-Hero-videoPlayerButton"
                    onClick={handleVideoStateChange}
                  >
                    {videoButtonLabel}
                  </Button>
                </Box>

                <Box className="lp-Hero-head hide-reduced-motion">
                  <div className="lp-Hero-headSize">
                    <div className="lp-Hero-headBlink js-copilot-head-wrapper">
                      <canvas className="js-copilot-head" data-analytics-event={copilotHydroTracking} />
                    </div>
                  </div>
                </Box>
              </Box>

              <LogoSuite hasDivider={false}>
                <LogoSuite.Heading visuallyHidden>GitHub Copilot is used by</LogoSuite.Heading>
                <LogoSuite.Logobar marquee marqueeSpeed="slow">
                  <Image src="/images/modules/site/copilot/logos/coca-cola.svg" alt="Coca Cola's logo" height={40} />
                  <Image
                    src="/images/modules/site/copilot/logos/coyote-logistics.svg"
                    alt="Coyote Logistics's logo"
                    height={40}
                  />
                  <Image src="/images/modules/site/copilot/logos/duolingo.svg" alt="Duolingo's logo" height={40} />
                  <Image src="/images/modules/site/copilot/logos/stripe.svg" alt="Stripe's logo" height={44} />
                  <Image src="/images/modules/site/copilot/logos/shopify.svg" alt="Shopify's logo" height={40} />
                  <Image
                    src="/images/modules/site/copilot/logos/mercado-libre.svg"
                    alt="Mercado Libre's logo"
                    height={40}
                  />
                  <Image src="/images/modules/site/copilot/logos/mercedes.svg" alt="Mercedes Benz's logo" height={48} />
                  <Image src="/images/modules/site/copilot/logos/fidelity.svg" alt="Fidelity's logo" height={36} />
                  <Image src="/images/modules/site/copilot/logos/dell.svg" alt="Dell's logo" height={60} />
                  <Image src="/images/modules/site/copilot/logos/lemonade.svg" alt="Lemonade's logo" height={36} />
                  <Image
                    src="/images/modules/site/copilot/logos/sas.svg"
                    alt="Scandinavian Airlines's logo"
                    height={36}
                  />
                  <Image src="/images/modules/site/copilot/logos/itau.svg" alt="Itau's logo" height={50} />
                  <Image src="/images/modules/site/copilot/logos/hover.svg" alt="Hover's logo" height={36} />
                  <Image src="/images/modules/site/copilot/logos/lyft.svg" alt="Lyft's logo" height={64} />
                </LogoSuite.Logobar>
              </LogoSuite>
            </Grid.Column>
          </Grid>

          <Box style={{height: 0}}>
            <AnchorNav hideUntilSticky>
              <AnchorNav.Link href="#enterprise">Enterprise-ready</AnchorNav.Link>
              <AnchorNav.Link href="#features">Features</AnchorNav.Link>
              <AnchorNav.Link href="#pricing">Pricing</AnchorNav.Link>
              <AnchorNav.Link href="#faq">FAQs</AnchorNav.Link>
              {isCopilotPlansPageEnabled ? (
                <AnchorNav.Action href="/features/copilot/plans">Get started</AnchorNav.Action>
              ) : (
                <AnchorNav.Action href={copilotHeroSignupPath}>Buy now</AnchorNav.Action>
              )}
            </AnchorNav>
          </Box>
          <div className="lp-Hero-cover" />
        </section>

        <section id="enterprise" className="lp-Section lp-Section--level-1">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Enterprise-ready
                </SectionIntro.Label>
                <SectionIntro.Heading size="1" weight="bold">
                  The competitive advantage developers ask for by name.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Bento className="Bento Bento--inset">
                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Proven to increase developer productivity and accelerate the pace of software development.
                    </Bento.Heading>
                    <Link
                      href="https://github.blog/2022-09-07-research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/"
                      size="large"
                      variant="default"
                    >
                      Read the research
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image
                      src="/images/modules/site/copilot/enterprise-1.jpg"
                      alt=""
                      width="724"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="lp-Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="display" className="is-sansSerifAlt">
                      55%
                    </Bento.Heading>
                    <Text as="p" size="400" weight="medium" variant="muted" style={{marginBottom: '0'}}>
                      Faster coding
                    </Text>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image
                      src="/images/modules/site/copilot/enterprise-2.webp"
                      alt=""
                      width="492"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="lp-Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Designed by leaders in AI so you can build with confidence.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image
                      src="/images/modules/site/copilot/enterprise-3.webp"
                      alt=""
                      width="492"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Committed to your privacy, security, and trust.
                    </Bento.Heading>
                    <Link href="https://resources.github.com/copilot-trust-center/" size="large" variant="default">
                      Visit the Copilot Trust Center
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image
                      src="/images/modules/site/copilot/enterprise-4.jpg"
                      alt=""
                      width="724"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 5, small: 5, medium: 4, large: 4, xlarge: 5}}
                  flow={{xsmall: 'row', medium: 'column'}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Duolingo empowers its engineers to be force multipliers for expertise with GitHub Copilot,
                      Codespaces.
                    </Bento.Heading>
                    <Link href="https://github.com/customer-stories/duolingo" size="large" variant="default">
                      Read customer story
                    </Link>
                  </Bento.Content>
                  <Bento.Visual padding="normal">
                    <Image
                      src="/images/modules/site/copilot/enterprise-5.jpg"
                      alt="Mobile device using Duolingo app"
                      width="570"
                      height="532"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section
          id="productivity"
          className="lp-Section lp-Section--compact lp-Section--level-gradient lp-Section--illu"
        >
          <Grid
            className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap lp-Section--illu-content"
            style={{paddingBottom: '32px'}}
          >
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Heading size="3">
                  The industry <br className="break-whenNarrow" /> standard.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <AnimationProvider runOnce={true}>
                <Stack
                  direction={{narrow: 'vertical', regular: 'horizontal'}}
                  gap={32}
                  padding="none"
                  className="lp-Productivity"
                >
                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      37,000+
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Businesses have adopted GitHub Copilot
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      1 in 3
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Fortune 500 companies use GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Available for business since Dec 2022
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      55%
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Developer preference for GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Stack Overflow 2023 Survey
                    </Text>
                  </Box>
                </Stack>
              </AnimationProvider>
            </Grid.Column>
          </Grid>

          <Image
            src="/images/modules/site/copilot/productivity-bg-2.webp"
            alt=""
            width="600"
            height="350"
            className="lp-Productivity-bg--kite2"
            loading="lazy"
          />
          <Image
            src="/images/modules/site/copilot/productivity-bg-1.webp"
            alt=""
            width="706"
            height="338"
            className="lp-Productivity-bg--kite1"
            loading="lazy"
          />
          <Image
            src="/images/modules/site/copilot/productivity-bg-head.png"
            alt=""
            width="234"
            height="204"
            className="lp-Section--illu-img lp-Productivity-bg--mascot"
            loading="lazy"
          />
        </section>

        <section id="features" className="lp-Section lp-Section--noBottomPadding">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Features
                </SectionIntro.Label>
                <SectionIntro.Heading as="h2" size="1" weight="bold">
                  The AI coding assistant <br className="break-whenWide" /> elevating developer workflows.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <RiverBreakout style={{paddingTop: '0'}}>
                <a
                  href="https://docs.github.com/en/copilot/github-copilot-chat/about-github-copilot-chat#use-cases-for-github-copilot-chat"
                  data-analytics-event={copilotConvoHydroTracking}
                >
                  <RiverBreakout.A11yHeading>Accelerate workflows</RiverBreakout.A11yHeading>
                  <RiverBreakout.Visual aria-hidden="true">
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-breakout.mp4"
                      poster="/images/modules/site/copilot/features-breakout-poster.webp"
                      width="1248"
                      height="647"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-breakout-sm.mp4"
                      poster="/images/modules/site/copilot/features-breakout-poster-sm.webp"
                      width="350"
                      height="380"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-breakout.webp"
                      alt="Screenshot showing GitHub Copilot"
                      width="1248"
                      height="647"
                      style={{display: 'block'}}
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </RiverBreakout.Visual>
                </a>
                <RiverBreakout.Content
                  trailingComponent={() => (
                    <Timeline className="lp-Timeline">
                      <Timeline.Item>
                        <em>Improve code quality and security.</em> Developers feel{' '}
                        <a
                          className="lp-Link--inline"
                          href="https://github.blog/2023-10-10-research-quantifying-github-copilots-impact-on-code-quality/"
                        >
                          more confident in their code quality
                        </a>{' '}
                        when authoring code with Copilot. And with the built-in{' '}
                        <a
                          className="lp-Link--inline"
                          href="https://github.blog/2023-02-14-github-copilot-now-has-a-better-ai-model-and-new-capabilities/#filtering-out-security-vulnerabilities-with-a-new-ai-system"
                        >
                          vulnerability prevention system
                        </a>
                        , insecure coding patterns get blocked in real time.
                      </Timeline.Item>
                      <Timeline.Item>
                        <em>Enable greater collaboration.</em> Copilot’s the newest member of your team. You can ask
                        general programming questions or very specific ones about your codebase to get answers fast,
                        learn your way around, explain a mysterious regex, or get suggestions on how to improve legacy
                        code.
                      </Timeline.Item>
                    </Timeline>
                  )}
                >
                  <Text>
                    <em>Start a conversation about your codebase.</em> Whether you’re hunting down a bug or designing a
                    new feature—when you’re stuck, ask <br className="break-whenWide" />
                    GitHub Copilot.
                  </Text>
                </RiverBreakout.Content>
              </RiverBreakout>

              <River imageTextRatio="60:40">
                <River.Visual aria-hidden="true">
                  <a
                    href="https://docs.github.com/en/copilot/quickstart#introduction"
                    data-analytics-event={copilotSuggestionsHydroTracking}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-1.mp4"
                      poster="/images/modules/site/copilot/features-river-1-poster.webp"
                      width="708"
                      height="472"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-1-sm.mp4"
                      poster="/images/modules/site/copilot/features-river-1-poster-sm.webp"
                      width="350"
                      height="290"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-river-1.webp"
                      alt="Editor with GitHub Copilot Chat"
                      width="708"
                      height="472"
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </a>
                </River.Visual>
                <River.Content>
                  <Heading as="h3">Get AI-based suggestions in real time.</Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    GitHub Copilot suggests code completions as developers type and turns natural language prompts into
                    coding suggestions based on the project&apos;s context and style conventions.
                  </Text>
                </River.Content>
              </River>

              <River imageTextRatio="60:40">
                <River.Visual aria-hidden="true">
                  <a
                    href="https://docs.github.com/en/copilot/github-copilot-enterprise/copilot-docset-management/about-copilot-docset-management"
                    data-analytics-event={copilotDocsHydroTracking}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-2.mp4"
                      poster="/images/modules/site/copilot/features-river-2-poster.webp"
                      width="708"
                      height="472"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-2-sm.mp4"
                      poster="/images/modules/site/copilot/features-river-2-poster-sm.webp"
                      width="350"
                      height="290"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-river-2.webp"
                      alt="GitHub Copilot showing different credential sources"
                      width="708"
                      height="472"
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </a>
                </River.Visual>
                <River.Content>
                  <Heading as="h3">
                    <Label className="lp-Label--inRiver">Copilot Enterprise</Label>
                    Docs that feel tailored for you.
                  </Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    Spend less time searching and more time learning, by getting personalized answers that are grounded
                    in your organization’s documentation, with inline citations. Load content → Ask question → Profit.
                  </Text>
                </River.Content>
              </River>

              <River imageTextRatio="60:40" style={{paddingBottom: '0'}}>
                <River.Visual aria-hidden="true">
                  <a
                    href="https://docs.github.com/en/copilot/github-copilot-enterprise/copilot-pull-request-summaries/about-copilot-pull-request-summaries"
                    data-analytics-event={copilotPullRequestsHydroTracking}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-3.mp4"
                      poster="/images/modules/site/copilot/features-river-3-poster.webp"
                      width="708"
                      height="472"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-3-sm.mp4"
                      poster="/images/modules/site/copilot/features-river-3-poster-sm.webp"
                      width="350"
                      height="290"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-river-3.webp"
                      alt="GitHub Copilot adding a summary to a pull request"
                      width="708"
                      height="472"
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </a>
                </River.Visual>
                <River.Content>
                  <Heading as="h3">
                    <Label className="lp-Label--inRiver">Copilot Enterprise</Label>
                    Pull requests that tell a story.
                  </Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    GitHub Copilot keeps track of your work, suggests descriptions, and helps reviewers reason about
                    your changes.
                  </Text>
                </River.Content>
              </River>
            </Grid.Column>

            {/* Spacer */}
            <Box paddingBlockStart={{narrow: 96, regular: 128}} aria-hidden={true} />

            <Grid.Column span={12}>
              <Bento className="Bento Bento--raised">
                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 8, small: 6, medium: 5, large: 5}}
                  visualAsBackground
                  className="lp-Features-bento-5"
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    className="lp-Features-bento-5-content"
                  >
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      <Label className="lp-Label--inBento">Copilot Enterprise</Label>
                      Prefer bespoke? Fine-tune a private copilot for the precision enterprises demand.
                    </Bento.Heading>
                    <Link href="https://github.co/copilot-custom-models" size="large" variant="default">
                      Join the waitlist
                    </Link>
                  </Bento.Content>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} verticalAlign="start">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Ask for assistance right in your terminal.
                    </Bento.Heading>
                    <Link
                      href="https://docs.github.com/copilot/github-copilot-in-the-cli"
                      size="large"
                      variant="default"
                    >
                      Try Copilot in the CLI
                    </Link>
                  </Bento.Content>
                  <Bento.Visual position="0% 50%">
                    <Image
                      src="/images/modules/site/copilot/features-bento-1.webp"
                      alt="Screenshot of GitHub Copilot CLI in a terminal"
                      width="724"
                      height="620"
                      className="lp-Features-bento-1-visual"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} horizontalAlign="center">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Keep flying with your favorite editor.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    className="lp-Features-editorContainer"
                  >
                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilot"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-vscode.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        VS Code
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://docs.github.com/copilot/getting-started-with-github-copilot?tool=vimneovim"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-neovim.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        Neovim
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilotvs"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-visualstudio.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        Visual Studio
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://plugins.jetbrains.com/plugin/17718-github-copilot"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-jetbrains.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        JetBrains IDEs
                      </Text>
                    </Button>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5, large: 5, xlarge: 5}}
                  rowSpan={{xsmall: 4, small: 4, medium: 4, xlarge: 5}}
                  className="Bento-item"
                >
                  <Bento.Content
                    horizontalAlign={{xsmall: 'center', large: 'start'}}
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    leadingVisual={<DeviceMobileIcon />}
                    className="lp-Features-mobile"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold" className="lp-Features-mobileText">
                      Coming soon to <br className="break-whenNarrow" /> GitHub Mobile.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    horizontalAlign="center"
                    verticalAlign="end"
                    style={{columnGap: '24px', flexWrap: 'wrap'}}
                  >
                    <a href="https://play.google.com/store/apps/details?id=com.github.android">
                      <Image
                        src="/images/modules/site/copilot/features-bento-4-google.png"
                        alt="Google Play Store logo"
                        width="180"
                        height="53"
                        style={{display: 'block'}}
                        loading="lazy"
                      />
                    </a>
                    <a href="https://apps.apple.com/app/github/id1477376905?ls=1">
                      <Image
                        src="/images/modules/site/copilot/features-bento-4-apple.png"
                        alt="Apple App Store logo"
                        width="179"
                        height="53"
                        style={{display: 'block'}}
                        loading="lazy"
                      />
                    </a>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7, large: 7, xlarge: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Visual position="25% 0%">
                    <Image
                      src="/images/modules/site/copilot/features-bento-3.webp"
                      alt="A phone showing GitHub Copilot in GitHub Mobile"
                      width="724"
                      height="560"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section id="pricing" className="lp-Section lp-Section--pricing" ref={sectionPricingRef}>
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Pricing
                </SectionIntro.Label>
                <SectionIntro.Heading size="1" weight="bold">
                  Take flight with GitHub Copilot.
                </SectionIntro.Heading>
                <SectionIntro.Description className="lp-SectionIntro-description--wide">
                  Organizations and developers all over the world use GitHub Copilot to code faster, drive impact, and
                  focus on doing what matters most: building great software.
                </SectionIntro.Description>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="lp-Pricing-groupTitle">
                For organizations
              </Heading>

              <Grid className="lp-Pricing">
                <Grid.Column span={12} className="lp-Pricing-item has-BlurredBg has-GradientBorder">
                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap="none"
                    padding="none"
                    className="lp-Pricing--business"
                  >
                    {/* Copilot */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="lp-Label--pricing">
                          Recommended
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Business
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                          Copilot in the coding environment.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                        <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          19
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="lp-Pricing-divider" />

                      <UnorderedList variant="checked" className="lp-Pricing-list">
                        <UnorderedList.Item>Code completions</UnorderedList.Item>
                        <UnorderedList.Item>
                          Chat in IDE <sup>1</sup> and Mobile <sup>2</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>
                          CLI assistance <sup>3</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>Security vulnerability filter</UnorderedList.Item>
                        <UnorderedList.Item>Code referencing</UnorderedList.Item>
                        <UnorderedList.Item>Public code filter</UnorderedList.Item>
                        <UnorderedList.Item>IP indemnity</UnorderedList.Item>
                        <UnorderedList.Item>Enterprise-grade security, safety, and privacy</UnorderedList.Item>
                      </UnorderedList>

                      <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                        <Button as="a" href={copilotForBusinessSignupPath} block variant="primary">
                          Buy now
                        </Button>
                        <Button as="a" href={copilotContactSalesPath} block className="lp-Button--onColoredBg">
                          Contact sales
                        </Button>
                      </Stack>
                    </Stack>

                    <hr className="lp-Pricing-divider lp-Pricing-divider--business" />

                    {/* Copilot Enterprise */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="lp-Label--pricing">
                          Available Feb 2024
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Enterprise
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                          Copilot personalized to your organization throughout the software development lifecycle.
                          Requires GitHub Enterprise Cloud.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                        <Text size="500" weight="normal" className="is-sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          39
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="lp-Pricing-divider" />

                      <Box style={{flex: '1'}}>
                        <Box className="lp-Pricing-plus">
                          <ArrowLeftIcon size={16} />
                          <Text size="200" weight="normal" className="lp-Pricing-plusText">
                            Everything in Copilot, plus:
                          </Text>
                        </Box>

                        <UnorderedList variant="checked" className="lp-Pricing-list">
                          <UnorderedList.Item>Chat personalized to your codebase</UnorderedList.Item>
                          <UnorderedList.Item>Documentation search and summaries</UnorderedList.Item>
                          <UnorderedList.Item>Pull request summaries</UnorderedList.Item>
                          <UnorderedList.Item>Code review skills</UnorderedList.Item>
                          <UnorderedList.Item>
                            Fine-tuned models <sup>4</sup>
                          </UnorderedList.Item>
                        </UnorderedList>
                      </Box>
                      <Button as="a" href="https://github.com/features/preview/copilot-enterprise" variant="primary">
                        Join waitlist
                      </Button>
                    </Stack>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="lp-Pricing-groupTitle lp-Pricing-groupTitle--individuals">
                For individuals
              </Heading>

              <Grid className="lp-Pricing">
                <Grid.Column
                  span={12}
                  className="lp-Pricing-item has-BlurredBg has-GradientBorder"
                  style={{gap: '16px', backgroundColor: 'rgba(22, 27, 34, 0.85)'}}
                >
                  <Heading as="h4" size="5">
                    Copilot Individual
                  </Heading>

                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap={24}
                    padding="none"
                    className="lp-Pricing--multiColumn"
                  >
                    <Box style={{flex: '1'}}>
                      <Box marginBlockEnd={32}>
                        <Text as="p" size="200" weight="normal" variant="muted">
                          Code completions, Chat, and more for indie developers and freelancers.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none">
                        <Text size="500" weight="normal" className="is-sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          10
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per month / <br className="break-whenWide" />
                          $100 per year
                        </Text>
                      </Stack>
                    </Box>

                    <Box style={{flex: '1'}}>
                      <Button as="a" href={copilotSignupPath} block variant="primary">
                        Start a free trial
                      </Button>
                      <Text
                        as="p"
                        size="100"
                        weight="normal"
                        variant="muted"
                        style={{marginTop: 'var(--base-size-32)'}}
                      >
                        Free for verified students, teachers, and maintainers of popular open source projects.
                      </Text>
                    </Box>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid>

          {/* Spacer */}
          <Box paddingBlockStart={32} aria-hidden={true} />

          <div className="lp-Pricing-bgCover">
            <Image
              className="lp-Pricing-bgCoverImage"
              src="/images/modules/site/copilot/pricing-cover.svg"
              loading="lazy"
              alt=""
            />
          </div>
        </section>

        <section id="resources" className="lp-Section lp-Section--compact">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Heading size="3">Get the most out of GitHub Copilot.</SectionIntro.Heading>
              </SectionIntro>

              <Stack
                direction={{narrow: 'vertical', regular: 'horizontal', wide: 'horizontal'}}
                gap="normal"
                padding="none"
              >
                <div className="lp-Resources-card">
                  <Card ctaText="Explore GitHub Expert Services" href="https://github.com/services/">
                    <Card.Icon icon={<PeopleIcon />} color="purple" hasBackground />
                    <Card.Heading>Hands-on consulting, guided workshops, and training.</Card.Heading>
                    <Card.Description>
                      Insights, best practices, and knowledge to help you adopt GitHub quickly and efficiently.
                    </Card.Description>
                  </Card>
                </div>

                <div className="lp-Resources-card">
                  <Card ctaText="Read customer stories" href="https://github.com/customer-stories">
                    <Card.Icon icon={<BookIcon />} color="purple" hasBackground />
                    <Card.Heading>Meet the companies who build with GitHub.</Card.Heading>
                    <Card.Description>
                      Leading organizations choose GitHub to plan, build, secure and ship software.
                    </Card.Description>
                  </Card>
                </div>

                <div className="lp-Resources-card">
                  <Card ctaText="Read blog" href="https://github.blog/">
                    <Card.Icon icon={<BookIcon />} color="purple" hasBackground />
                    <Card.Heading>Keep up with the latest on GitHub and trends in AI.</Card.Heading>
                    <Card.Description>
                      Check out the GitHub blog for tips, technical guides, best practices, and more.
                    </Card.Description>
                  </Card>
                </div>
              </Stack>
            </Grid.Column>
          </Grid>
        </section>

        <section id="faq" className="lp-Section lp-Section--level-1">
          <Grid>
            <Grid.Column span={12} className="lp-FAQs">
              <FAQGroup>
                <FAQGroup.Heading>
                  Frequently asked <br /> questions.
                </FAQGroup.Heading>

                <FAQ>
                  <FAQ.Heading>General</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>What is GitHub Copilot?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot transforms the developer experience. Backed by the leaders in AI, Copilot
                        provides contextualized assistance throughout the software development lifecycle, from code
                        completions and chat assistance in the IDE to code explanations and answers to docs in GitHub
                        and more. With Copilot elevating their workflow, developers can focus on more: value,
                        innovation, and happiness.
                      </p>
                      <p>
                        Copilot enables developers to focus more energy on problem solving and collaboration and spend
                        less effort on the mundane and boilerplate. That’s why developers who use Copilot report up to{' '}
                        <a href="https://github.blog/2022-09-07-research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/">
                          75% higher satisfaction with their jobs than those who don’t and are up to 55% more productive
                          at writing code
                        </a>{' '}
                        without sacrifice to quality, which all adds up to engaged developers shipping great software
                        faster.
                      </p>
                      <p>
                        Copilot integrates with leading editors, including Visual Studio Code, Visual Studio, JetBrains
                        IDEs, and Neovim, and, unlike other AI coding assistants, is natively built into GitHub.
                        Although code completion functionality is available across all these extensions, the chat is
                        currently available only in Visual Studio Code and Visual Studio, with a beta version available
                        for JetBrains IDEs. Growing to millions of individual users and tens of thousands of business
                        customers, Copilot is the world’s most widely adopted AI developer tool and the competitive
                        advantage developers ask for by name.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      What are the differences between the GitHub Copilot Business, GitHub Copilot Enterprise, and
                      GitHub Copilot Individual plans?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot has multiple offerings for organizations and an offering for individual
                        developers. All the offerings include both code completion and chat assistance. The primary
                        differences between the organization offerings and the individual offering are license
                        management, policy management, and IP indemnity.
                      </p>
                      <p>
                        Organizations can choose between GitHub Copilot Business and GitHub Copilot Enterprise, which is
                        coming in February 2024. GitHub Copilot Business primarily features GitHub Copilot in the coding
                        environment - that is the IDE and CLI. In early-2024, it will also include GitHub Copilot in
                        GitHub Mobile. GitHub Copilot Enterprise includes everything in GitHub Copilot Business and adds
                        an additional layer of personalization for organizations as well as GitHub Copilot integrated
                        into GitHub as a chat interface to allow developers to converse about their codebase and action
                        buttons throughout the platform. GitHub Copilot Enterprise can index an organization’s codebase
                        for a deeper understanding of the customer’s knowledge for more tailored suggestions and will
                        offer customers access to fine-tune custom, private models for code completion.
                      </p>
                      <p>
                        GitHub Copilot Individual is designed for individual developers, freelancers, students,
                        educators, and open source maintainers. The plan includes all the features of GitHub Copilot
                        Business except organizational license management, policy management, and IP indemnity.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>What languages, IDEs, and platforms does GitHub Copilot support?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot is trained on all languages that appear in public repositories. For each
                        language, the quality of suggestions you receive may depend on the volume and diversity of
                        training data for that language. For example, JavaScript is well-represented in public
                        repositories and is one of GitHub Copilot’s best supported languages. Languages with less
                        representation in public repositories may produce fewer or less robust suggestions.
                      </p>
                      <p>
                        GitHub Copilot is available as an extension in Visual Studio Code, Visual Studio, Vim, Neovim,
                        the JetBrains suite of IDEs, and Azure Data Studio. Although code completion functionality is
                        available across all these extensions, the chat is currently available only in Visual Studio
                        Code and Visual Studio, with a beta version available for JetBrains IDEs. GitHub Copilot is also
                        supported in terminals through GitHub CLI. GitHub Copilot will soon be natively integrated into
                        GitHub web and mobile.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>What data has GitHub Copilot been trained on?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot is powered by generative AI models developed by GitHub, OpenAI, and Microsoft. It
                        has been trained on natural language text and source code from publicly available sources,
                        including code in public repositories on GitHub.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot “copy/paste”?</FAQ.Question>
                    <FAQ.Answer>
                      <p>No, GitHub Copilot generates suggestions using probabilistic reasoning.</p>
                      <ul>
                        <li>
                          When thinking about intellectual property and open source issues, it is critical to understand
                          how GitHub Copilot really works. The AI models that create Copilot’s suggestions may be
                          trained on public code, but do not contain any code. When they generate a suggestion, they are
                          not “copying and pasting” from any codebase.
                        </li>
                        <li>
                          To generate a suggestion for code completion, Copilot begins by examining the code in your
                          editor—focusing on the lines just before and after your cursor, but also information in other
                          files open in your editor. That information is sent to Copilot’s model, to make a
                          probabilistic determination of what is likely to come next and generate suggestions.
                        </li>
                        <li>
                          To generate a suggestion from chat, such as providing an answer to a question from your chat
                          prompt, Copilot creates a Contextual Prompt by combining (1) a “context summary” with (2) the
                          question you submit to the Copilot Chat interface in your IDE. The contextual prompt is then
                          sent to Copilot’s model to make a probabilistic determination of what is likely to come next
                          and generate suggestions.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>What data does GitHub use to create a Contextual Prompt?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot Chat creates a Contextual Prompt by combining (1) a “context summary” with (2)
                        the question you submit to your editor.
                      </p>
                      <p>
                        For instance, if you submit “what does this method do” the client for GitHub Copilot Chat will
                        automatically examine the context from your active documents in the editor to determine what you
                        mean by the word “this.” It will then compose a suitable question to pose to the GitHub Copilot
                        Chat model that automatically includes that selection, without you needing to copy and paste
                        code into the chat window, saving you time and delivering a hopefully useful response.
                      </p>
                      <p>
                        Depending on the question you ask, the GitHub Copilot Chat client will automatically use
                        appropriate aspects of your context to form the question. The information it uses may include:
                      </p>
                      <ul>
                        <li>The code file open in your active document</li>
                        <li>Your selection (or “code blocks for the current cursor position”) in the document</li>
                        <li>Summaries of related documents open in your editor or from the workspace</li>
                        <li>Information about errors/warnings/messages/exceptions in your error list</li>
                        <li>General workspace information, such as frameworks, languages, and dependencies</li>
                        <li>Parts of related files in your workspace/project/repo</li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>

                <FAQ>
                  <FAQ.Heading>Privacy</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>
                      Where can I learn about GitHub Copilot privacy, security, and responsible AI policies?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        You can visit the{' '}
                        <a href="https://resources.github.com/copilot-trust-center/">GitHub Copilot Trust Center</a> for
                        more information on these topics.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>What personal data does GitHub Copilot process?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot leverages three categories of personal data: user engagement data, prompts, and
                        suggestions.
                      </p>
                      <p>
                        <strong>User engagement data</strong>
                        <br />
                        User engagement data is usage information about events generated when interacting with a code
                        editor. These events include user edit actions (for example completions accepted and dismissed),
                        error messages, interactions with Chat UI, and general usage data to identify user metrics such
                        as latency and feature engagement. This information may include personal data, such as
                        pseudonymous identifiers.
                      </p>
                      <p>
                        <strong>Prompts</strong>
                        <br />A prompt is the collection of code and supporting contextual information that the GitHub
                        Copilot extension sends to GitHub to generate suggestions. A prompt is typically generated in
                        one of two ways. In (1), to generate a suggestion for code completion the extension sends a
                        prompt when a user working on a file pauses typing, or uses a designated keyboard shortcut to
                        request a suggestion. In (2), to generate a suggestion for chat, a prompt is also generated when
                        a user submits a query to GitHub Copilot chat.
                      </p>
                      <p>
                        <strong>Suggestions</strong>
                        <br />A suggestion is one or more lines of proposed code and other output returned to the
                        Copilot extension after a prompt is received and processed by the AI models that power GitHub
                        Copilot. Suggestions come in two types: as code completions in your editor, and as responses to
                        your chat queries.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      Does GitHub Copilot for Business use my Prompts or Suggestions to train the AI model?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>No, GitHub Copilot for Business does not use Prompts or Suggestions to train the AI model.</p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How else is personal data in GitHub Copilot Business used?</FAQ.Question>
                    <FAQ.Answer>
                      <p>GitHub processes user engagement data to provide the service, including:</p>
                      <ul>
                        <li>
                          To deliver functional capabilities as licensed, configured, and used by the customer and its
                          users, including providing personalized user experiences;
                        </li>
                        <li>Troubleshooting (preventing, detecting, and repairing problems); and</li>
                        <li>
                          Keeping products up to date and performant, and enhancing, reliability, efficacy, quality,
                          security, and user productivity.
                        </li>
                      </ul>
                      <p>
                        Per Customer instruction in the GitHub DPA, GitHub processes user engagement data as a data
                        controller for the purposes of:
                      </p>
                      <ul>
                        <li>Billing and account management;</li>
                        <li>Compensation such as calculating employee commissions and partner incentives;</li>
                        <li>
                          Aggregated internal reporting and business modeling, such as forecasting, revenue, capacity
                          planning, and product strategy; and
                        </li>
                        <li>Aggregated financial reporting.</li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How long does GitHub Copilot retain personal data?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        <strong>User engagement data</strong>
                        <br />
                        User engagement data is retained by GitHub for 24 months.
                      </p>
                      <p>
                        <strong>Prompts</strong>
                        <br />
                        For users of Copilot (code completions and chat) in the IDE, prompts are discarded by the
                        service once a suggestion is returned to the user. By default, prompts are not stored for
                        Copilot Business users. Copilot Individual users may opt out of allowing GitHub to retain
                        prompts.
                      </p>
                      <p>
                        <strong>Suggestions</strong>
                        <br />
                        For users of Copilot (code completions and chat) in the IDE, suggestions are discarded by the
                        service once it is returned to the user. By default, suggestions in the IDE are not stored for
                        Copilot Business users (both code completions, and chat). Copilot Individual users may opt out
                        of allowing GitHub to retain suggestions.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How are the transmitted Prompts and Suggestions protected?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We know that user edit actions, source code snippets, and URLs of repositories and file paths
                        are sensitive data. Consequently, several measures of protection are applied, including:
                      </p>
                      <ul>
                        <li>The transmitted data is encrypted in transit and at rest</li>
                        <li>
                          Access is strictly controlled. The data can only be accessed by (1) named GitHub personnel
                          working on the GitHub Copilot team or on the GitHub platform health team, (2) Microsoft
                          personnel working on or with the GitHub Copilot team, and (3) OpenAI personnel who work on
                          GitHub Copilot
                        </li>
                        <li>
                          Role-based access controls and multi-factor authentication are required for personnel
                          accessing code snippet data
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How can users of GitHub Copilot control use of their data?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        User Engagement Data (which includes pseudonymous identifiers and general usage data), is
                        required for the use of GitHub Copilot and will continue to be collected, processed, and shared
                        with Microsoft as you use GitHub Copilot.
                      </p>
                      <p>Prompts and Suggestions for GitHub Copilot Business users are not retained by GitHub.</p>
                      <p>
                        Users of GitHub Copilot Individual can choose whether Prompts and Suggestions are retained by
                        GitHub and further processed and shared with Microsoft by{' '}
                        <a href="https://github.com/settings/copilot">adjusting user settings</a>. Users of GitHub
                        Copilot Individual can request deletion of Prompts and Suggestions associated with their GitHub
                        identity by <a href="https://support.github.com/request">filling out a support ticket</a>.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Will my code be shared with other users?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        No. We follow responsible practices in accordance with our{' '}
                        <a href="https://docs.github.com/site-policy/privacy-policies/github-privacy-statement">
                          Privacy Statement
                        </a>{' '}
                        to ensure that your code snippets will not be used as suggested code for other users of GitHub
                        Copilot.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot ever output personal data?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Because the model powering GitHub Copilot was trained on publicly available code, its training
                        set included personal data that was included in that code. From our internal testing, we found
                        it to be very rare that GitHub Copilot suggestions included personal data verbatim from the
                        training set.
                      </p>
                      <p>
                        In some cases, the model will suggest what appears to be personal data – email addresses, phone
                        numbers, etc. – but those suggestions are actually fictitious information synthesized from
                        patterns in training data and therefore do not relate to any particular individual. For example,
                        when one of our engineers prompted GitHub Copilot with, “My name is Mona and my birthdate is,”
                        GitHub Copilot suggested a random, fictitious date of “December 12,” which is not Mona’s actual
                        birthdate.
                      </p>
                      <p>
                        We have also implemented a filter that blocks emails when shown in standard formats, but it’s
                        still possible to get the model to suggest this sort of content if you try hard enough. We will
                        keep improving the filter system to be more intelligent to detect and remove more personal data
                        from the GitHub Copilot suggestions.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      What if I’m accused of copyright infringement based on using a GitHub Copilot suggestion?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub will defend you as provided in the{' '}
                        <a href="https://github.com/customer-terms/github-copilot-product-specific-terms">
                          GitHub Copilot Product Specific Terms
                        </a>
                        .
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>

                <FAQ>
                  <FAQ.Heading>Responsible AI</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>
                      What are the intellectual property considerations when using GitHub Copilot?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        The primary IP considerations for GitHub Copilot relate to copyright. The model that powers
                        Copilot is trained on a broad collection of publicly accessible code, which may include
                        copyrighted code, and Copilot’s suggestions (in rare instances) may resemble the code its model
                        was trained on. Here’s some basic information you should know about these considerations:
                      </p>
                      <p>
                        Copyright law permits the use of copyrighted works to train AI models: Countries around the
                        world have provisions in their copyright laws that enable machines to learn, understand, extract
                        patterns, and facts from copyrighted materials, including software code. For example, the
                        European Union, Japan, and Singapore, have expressed provisions permitting machine learning to
                        develop AI models. Other countries including Canada, India, and the United States also permit
                        such training under their fair use/fair dealing provisions. GitHub Copilot’s AI model was
                        trained with the use of code from GitHub’s public repositories—which are publicly accessible and
                        within the scope of permissible copyright use.
                      </p>
                      <p>
                        What about copyright risk in suggestions? In rare instances (less than 1% based on GitHub’s
                        research), suggestions from GitHub may match examples of code used to train GitHub’s AI model.
                        LLMs like Copilot, generate suggestions based on ‘probability’ and not copying, since the models
                        do not contain copies of, or copy and paste, code from any source.
                      </p>
                      <p>
                        Our experience shows that matching suggestions are most likely to occur in two situations: (i)
                        when there is little or no context in the code editor for Copilot’s model to synthesize, or (ii)
                        when a matching suggestion represents a common approach or method. If a user accepts a Copilot
                        suggestion matching existing copyrighted code, there is risk that using that suggestion could
                        trigger claims of copyright infringement, which would depend on the amount and nature of code
                        used, and the context of how the code is used. In many ways, this is the same risk that arises
                        when using any code that a developer does not originate, such as copying code from an online
                        source, or reusing code from a library. That is why responsible organizations and developers
                        recommend that users employ code scanning policies to identify and evaluate potential matching
                        code.
                      </p>
                      <p>
                        In Copilot, you can opt whether to allow Copilot to suggest code completions that match publicly
                        available code on GitHub.com. For more information, see &quot;
                        <a href="https://docs-internal-46619-d1aa34.preview.ghdocs.com/en/copilot/configuring-github-copilot/configuring-github-copilot-settings-on-githubcom#enabling-or-disabling-duplication-detection">
                          Configuring GitHub Copilot settings on GitHub.com
                        </a>
                        &quot;. If you have allowed suggestions that match public code, GitHub Copilot can provide you
                        with details about the matching code when you accept such suggestions. Matching code does not
                        necessarily mean copyright infringement, so it is ultimately up to the user to determine whether
                        to use the suggestion, and what and who to attribute (along with other license compliance) in
                        appropriate circumstances.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot include a filtering mechanism to mitigate risk?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Yes, GitHub Copilot does include an optional code referencing filter to detect and suppress
                        certain suggestions that match public code on GitHub.
                      </p>
                      <ul>
                        <li>
                          GitHub has created a duplication detection filter to detect and suppress GitHub Copilot
                          suggestions that contain code that includes snippets of at least 150 characters that match
                          public code on GitHub. This filter is enabled by the administrator for your enterprise and it
                          can apply for all organizations within your enterprise or the administrator can defer control
                          to individual organizations. Based on your settings, this filter is applied to code
                          suggestions coming as part of the code completion functionality as well as to code blocks
                          suggested as part of chat suggestions.
                        </li>
                        <li>
                          With the filter enabled, Copilot checks code suggestions with its surrounding code for matches
                          or near matches (ignoring whitespace) against public code on GitHub of about 150 characters.
                          If there is a match, the suggestion will not be shown to the user.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      Does GitHub Copilot include features to make it easier for users to identify potentially relevant
                      open source licenses for matching suggestions?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Yes, GitHub Copilot is previewing a{' '}
                        <a href="https://github.blog/2023-08-03-introducing-code-referencing-for-github-copilot/">
                          code referencing feature
                        </a>{' '}
                        as an additional tool to assist users to find and review potentially relevant open source
                        licenses. Code referencing is currently available in Visual Studio Code. This feature searches
                        across public GitHub repositories for code that matches a Copilot suggestion. If there’s a
                        match, users will find its information displayed in the Copilot console log, including where the
                        match occurred, any applicable licenses, and a deep link to learn more. The deep link will take
                        users to a navigable page on GitHub.com to browse examples of the code match and their
                        repository licenses, and see how many repositories—including ones without licenses—that code
                        appears in, as well as links to those repositories. Copilot users can review this information to
                        determine whether the applicable suggestions are suitable for use, and whether additional
                        measures may be necessary to use them.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Who owns the suggestions provided by GitHub Copilot?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We don’t determine whether a suggestion is capable of being owned, but we are clear that GitHub
                        does not claim ownership of a suggestion.
                      </p>
                      <p>
                        Whether a suggestion generated by an AI model can be owned depends on many factors (e.g. the
                        intellectual property law in the relevant country, the length of the suggestion, the extent that
                        suggestion is considered ‘functional’ instead of expressive, etc).
                      </p>
                      <ul>
                        <li>
                          If a suggestion is capable of being owned, our terms are clear: GitHub does not claim
                          ownership.
                        </li>
                        <li>
                          GitHub does not claim ownership of any suggestion. In certain cases, it is possible for
                          Copilot to produce similar suggestions to different users. For example, two unrelated users
                          both starting new files to code the quicksort algorithm in Java will likely get the same
                          suggestion. The possibility of providing similar suggestions to multiple users is a common
                          part of generative AI systems.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Can GitHub Copilot introduce insecure code in its suggestions?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Public code may contain insecure coding patterns, bugs, or references to outdated APIs or
                        idioms. When GitHub Copilot synthesizes code suggestions based on this data, it can also
                        synthesize code that contains these undesirable patterns. Copilot has filters in place that
                        either block or notify users of insecure code patterns that are detected in Copilot suggestions.
                        These filters target the most common vulnerable coding patterns, including{' '}
                        <a href="https://cwe.mitre.org/data/definitions/798.html">hardcoded credentials</a>,{' '}
                        <a href="https://cwe.mitre.org/data/definitions/89.html">SQL injections</a>, and{' '}
                        <a href="https://cwe.mitre.org/data/definitions/22.html">path injections</a>. Additionally, in
                        recent years we’ve provided tools such as GitHub Advanced Security, GitHub Actions, Dependabot,
                        and CodeQL to open source projects to help improve code quality. Of course, you should always
                        use GitHub Copilot together with good testing and code review practices and security tools, as
                        well as your own judgment.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      Is GitHub Copilot intended to fully automate code generation and replace developers?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        No. Copilot is a tool intended to make developers more efficient. It’s not intended to replace
                        developers, who should continue to apply the same sorts of safeguards and diligence they would
                        apply with regard to any third-party code of unknown origin.
                      </p>
                      <ul>
                        <li>
                          The product is called “Copilot” not “Autopilot” and it’s not intended to generate code without
                          oversight. You should use exactly the same sorts of safeguards and diligence with Copilot’s
                          suggestions as you would use with any third-party code.
                        </li>
                        <li>
                          Identifying best practices for use of third party code is beyond the scope of this section.
                          That said, whatever practices your organization currently uses – rigorous functionality
                          testing, code scanning, security testing, etc. – you should continue these policies with
                          Copilot’s suggestions. Moreover, you should make sure your code editor or editor does not
                          automatically compile or run generated code before you review it.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Can GitHub Copilot users simply use suggestions without concern?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Not necessarily. GitHub Copilot users should align their use of Copilot with their respective
                        risk tolerances.
                      </p>
                      <ul>
                        <li>
                          As noted above, GitHub Copilot is not intended to replace developers, or their individual
                          skill and judgment, and is not intended to fully automate the process of code development. The
                          same risks that apply to the use of any third-party code apply to the use of Copilot’s
                          suggestions.
                        </li>
                        <li>
                          Depending on your particular use case, you should consider implementing the protections
                          discussed above. It is your responsibility to assess what is appropriate for the situation and
                          implement appropriate safeguards.
                        </li>
                        <li>
                          You’re entitled to IP indemnification from GitHub for the unmodified suggestions when
                          Copilot’s filtering is enabled. If you do elect to enable this feature, the copyright
                          responsibility is ours, not our customers. As part of our ongoing commitment to responsible
                          AI, GitHub and Microsoft extends our IP indemnity and protection support to our customers who
                          are empowering their teams with GitHub Copilot. Details{' '}
                          <a href="https://blogs.microsoft.com/on-the-issues/2023/09/07/copilot-copyright-commitment-ai-legal-concerns/">
                            here
                          </a>
                          .
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot support accessibility features?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We are conducting internal testing of GitHub Copilot’s ease of use by developers with
                        disabilities and working to ensure that GitHub Copilot is accessible to all developers. Please
                        feel free to share your feedback on GitHub Copilot accessibility in our{' '}
                        <a href="https://github.com/github-community/community/discussions/categories/copilot">
                          feedback forum
                        </a>
                        .
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot produce offensive outputs?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot includes filters to block offensive language in the prompts and to avoid
                        synthesizing suggestions in sensitive contexts. We continue to work on improving the filter
                        system to more intelligently detect and remove offensive outputs. If you see offensive outputs,
                        please report them directly to{' '}
                        <a href="mailto:copilot-safety@github.com">copilot-safety@github.com</a> so that we can improve
                        our safeguards. GitHub takes this challenge very seriously and we are committed to addressing
                        it.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Will GitHub Copilot work as well using languages other than English?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Given public sources are predominantly in English, GitHub Copilot will likely work less well in
                        scenarios where natural language prompts provided by the developer are not in English and/or are
                        grammatically incorrect. Therefore, non-English speakers might experience a lower quality of
                        service.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>

                <FAQ>
                  <FAQ.Heading>Upcoming features and offerings</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>How does the GitHub Copilot Enterprise waitlist work?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        By joining the GitHub Copilot Enterprise waitlist, you are expressing your intent to be
                        contacted about the upcoming GitHub Copilot offering. You may also get an opportunity to test a
                        pre-release version of the product. If you are an administrator or member of a GitHub Enterprise
                        Cloud account, you will be able to nominate a specific enterprise to try GitHub Copilot
                        Enterprise in private beta.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How does the GitHub Copilot Customization waitlist work?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        By joining the GitHub Copilot Customization waitlist, you are expressing your intent to be
                        contacted about the upcoming GitHub Copilot offering. You may also get an opportunity to test a
                        pre-release version of the product. If you are an administrator for an organization using GitHub
                        Enterprise Cloud or GitHub Copilot Business, you will be able to nominate a specific
                        organization to try GitHub Copilot Customization in private beta.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      I’m currently participating in or am on the waitlist for the GitHub Next technical preview for
                      GitHub Copilot for CLI. What should I do now that there is a public beta for GitHub Copilot in the
                      CLI?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We recommend joining the public beta. You can find information on how to join{' '}
                        <a href="https://docs.github.com/copilot/github-copilot-in-the-cli">here</a>.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      I joined the public beta for GitHub Copilot in the CLI but the feature looks different than it did
                      during the GitHub Next technical preview. Why?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        The feedback we get from users during GitHub Next technical previews directly informs how we
                        refine those features before moving them into production. That’s one of the reasons technical
                        previews are so valuable, and it is not unusual for a feature to change when it moves from
                        GitHub Next to the GitHub product and engineering teams.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      GitHub announced a private beta of Github Copilot pull request summaries, but only through the new
                      GitHub Copilot Enterprise plan. What does that mean for users that are currently participating in
                      the GitHub Next technical preview for GitHub Copilot for Pull Requests or are on that waitlist?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We have closed the waitlist for the GitHub Next technical preview. Users that have already been
                        admitted to the technical preview can continue to experiment with GitHub Copilot for Pull
                        Requests. GitHub Copilot features for pull requests are currently limited to the GitHub Copilot
                        Enterprise plan. If you are a developer or administrator that’s part of an enterprise, you can
                        join the GitHub Copilot Enterprise waitlist{' '}
                        <a href="https://github.com/features/preview/copilot-enterprise">here</a>.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      GitHub announced a private beta of Github Copilot documentation search, but only through the new
                      GitHub Copilot Enterprise plan. What does that mean for users that are currently participating in
                      the GitHub Next technical preview for GitHub Copilot for Docs or are on that waitlist?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We have closed the waitlist for the GitHub Next technical preview. Users that have already been
                        admitted to the technical preview can continue to experiment with GitHub Copilot for Docs.
                        GitHub Copilot features for documentation are currently limited to the GitHub Copilot Enterprise
                        plan. If you are a developer or administrator that’s part of an enterprise, you can join the
                        <a href="https://github.com/features/preview/copilot-enterprise">
                          GitHub Copilot Enterprise waitlist
                        </a>
                        .
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>When will Copilot Chat be available on github.com?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Copilot Chat will be available on github.com to users that access GitHub Copilot through the
                        Copilot Enterprise plan when that plan becomes generally available in February.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>When will Copilot Chat be available on mobile?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We do not have a set timeline for making Copilot Chat available on mobile. We’ll continue to
                        update this page with the latest information on new capabilities for various plans.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>
              </FAQGroup>
            </Grid.Column>
          </Grid>
        </section>

        <section id="footnotes" className="lp-Section lp-Section--level-1" style={{paddingTop: '0'}}>
          <Grid className="lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <OrderedList>
                <OrderedList.Item className="lp-Footnotes-item">
                  Chat in Visual Studio Code and Visual Studio now generally available. Chat in JetBrains IDEs currently
                  in preview and generally available in 2024. Instructions to enable the preview can be found{' '}
                  <a
                    className="lp-Link--inline"
                    href="https://docs.github.com/copilot/github-copilot-chat/using-github-copilot-chat-in-your-ide?tool=jetbrains"
                  >
                    here
                  </a>
                  .
                </OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">Chat in GitHub Mobile is coming soon.</OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">
                  Currently in preview. Instructions to enable Copilot in the CLI in preview can be found{' '}
                  <a className="lp-Link--inline" href="https://docs.github.com/copilot/github-copilot-in-the-cli">
                    here
                  </a>
                  .
                </OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">
                  Currently in preview. Copilot Enterprise grants you access to customization for fine-tuned models
                  (available in 2024).
                </OrderedList.Item>
              </OrderedList>
            </Grid.Column>
          </Grid>
        </section>
      </ThemeProvider>
    </>
  )
}

try{ DefaultIndex.displayName ||= 'DefaultIndex' } catch {}
try{ AutoPlayVideo.displayName ||= 'AutoPlayVideo' } catch {}